import { Town } from "./towns";

export type ServiceKey = "roofing" | "siding" | "gutters";

export interface ServiceOffering {
  label: string;
  desc: string;
}
export interface MaterialOption {
  name: string;
  bestFor: string;
  benefits: string;
  maintenance: string;
}
export interface PricingFactor {
  factor: string;
  detail: string;
}
export interface ProcessStep {
  title: string;
  detail: string;
}
export interface CommonProblem {
  problem: string;
  solution: string;
}
export interface ProjectExample {
  title: string;
  problem: string;
  solution: string;
  materials: string;
  result: string;
}
export interface FAQ {
  question: string;
  answer: string;
  /** Optional official reference links rendered after the answer (e.g. municipal permit pages). */
  links?: { label: string; url: string }[];
}
export interface PlanningPoint {
  title: string;
  detail: string;
}
export interface LinkItem {
  label: string;
  href?: string;
}

export interface GeoServiceConfig {
  key: ServiceKey;
  label: string;
  /** Icon key resolved to a lucide icon inside the composer. */
  iconKey: "shield" | "home" | "droplets";
  schemaType: string | string[];
  seoTitle: (t: Town) => string;
  seoDescription: (t: Town) => string;
  canonical: (t: Town) => string;
  breadcrumbs: (t: Town) => LinkItem[];
  h1: (t: Town) => string;
  heroSubtitle: (t: Town) => string;
  introParagraphs: (t: Town) => string[];
  servicesHeading: (t: Town) => string;
  servicesOffered: (t: Town) => ServiceOffering[];
  problemsHeading: (t: Town) => string;
  problemsIntro: (t: Town) => string;
  commonProblems: CommonProblem[];
  materialsHeading: (t: Town) => string;
  materialOptions: MaterialOption[];
  pricingHeading: (t: Town) => string;
  pricingIntro: (t: Town) => string;
  pricingFactors: PricingFactor[];
  processHeading: (t: Town) => string;
  processSteps: ProcessStep[];
  projectsHeading: (t: Town) => string;
  projectExamples: (t: Town) => ProjectExample[];
  planningHeading: (t: Town) => string;
  planningIntro: (t: Town) => string;
  planningPoints: PlanningPoint[];
  faqs: (t: Town) => FAQ[];
  midCTA: (t: Town) => { title: string; subtitle: string };
  finalCTA: (t: Town) => { title: string; subtitle: string };
  /** Service-specific related links (town-aware). */
  relatedLinks: (t: Town) => LinkItem[];
}

const PHONE = "717-725-7170";

// Compose a geo page <title>: prefer the keyword-rich variant when it stays
// display-safe (<= 60 chars), fall back to a short brand suffix, then to the
// base alone (base never exceeds ~60 with current town names).
const geoTitle = (base: string, keywords: string): string => {
  const withKeywords = `${base} | ${keywords}`;
  if (withKeywords.length <= 60) return withKeywords;
  const branded = `${base} | Graystone`;
  return branded.length <= 60 ? branded : base;
};

const isHistoric = (t: Town) =>
  /historic|victorian/i.test(`${t.housingCharacter} ${t.homeEra} ${t.uniqueLocalContext}`);

const isTreeHeavy = (t: Town) =>
  /tree|wooded|canopy|leaf|debris|oak|hardwood/i.test(`${t.treeCoverage} ${t.localPainPoints.join(" ")}`);

const sidingRoute = (t: Town) =>
  `/service-areas/siding-contractor-${t.slug}-pa/`;
const roofingRoute = (t: Town) =>
  `/service-areas/roofing-contractor-${t.slug}-pa/`;
const guttersRoute = (t: Town) =>
  `/service-areas/gutter-installation-${t.slug}-pa/`;

/** Brand-level differentiators (real, no fabricated claims). */
export const whyChooseUs: { title: string; desc: string }[] = [
  { title: "Locally Owned & Operated", desc: "Our team is personally involved in every project. You talk to the people doing the work, not a faceless sales team." },
  { title: "Local Since 2007", desc: "We've served homeowners across Lancaster, Chester, Berks, Dauphin, Cumberland, York, Montgomery, Delaware, and Bucks counties for over 18 years from our East Petersburg base." },
  { title: "Licensed & Insured", desc: "PA Home Improvement Contractor #PA034872, with full liability and workers' compensation coverage on every job." },
  { title: "A+ BBB Accredited", desc: "We've maintained an A+ Better Business Bureau rating and a 5.0-star Google reputation built on honest, reliable work." },
  { title: "Free Written Estimates", desc: "Detailed, itemized quotes with materials and scope spelled out, no high-pressure sales and no surprises." },
  { title: "Clean, Respectful Job Sites", desc: "We protect your property, magnet-sweep for nails, and leave your home cleaner than we found it." },
];

/** Real, verifiable trust signals only, no fabricated counts or certifications. */
export const trustSignals: string[] = [
  "PA Home Improvement Contractor #PA034872",
  "Fully licensed and insured",
  "Locally owned and operated since 2007 (18+ years)",
  "A+ BBB Accredited Business",
  "5.0-star Google and Yelp rating",
  "Free, no-pressure written estimates",
  "Manufacturer-backed material warranties",
];

const roofing: GeoServiceConfig = {
  key: "roofing",
  label: "Roofing",
  iconKey: "shield",
  schemaType: "RoofingContractor",
  // Lancaster city gets a hyper-local angle so it does not compete with the
  // homepage (brand + dual service) or the roofing hub (roofing company).
  seoTitle: (t) =>
    t.slug === "lancaster"
      ? "Lancaster City Roofing Contractor | Neighborhood Roof Repair"
      : geoTitle(`Roofing Contractor in ${t.name}, PA`, "Roof Repair & Replacement"),
  seoDescription: (t) =>
    t.slug === "lancaster"
      ? `Roof repair and replacement for Lancaster City neighborhoods, from downtown rowhomes to brick Victorians. Locally owned since 2007. Call ${PHONE}.`
      : `Roof replacement, roof repair, and free inspections in ${t.name}, PA from Graystone Roofing & Siding. Locally owned since 2007. Call ${PHONE}.`,
  canonical: (t) =>
    `https://lancasterparoofing.com/service-areas/roofing-contractor-${t.slug}-pa/`,
  breadcrumbs: (t) => [
    { label: "Roofing", href: "/services/roofing-company-lancaster-pa/" },
    { label: "Service Areas", href: "/service-areas/" },
    { label: `${t.name}, PA` },
  ],
  h1: (t) => `Roofing Contractor in ${t.name}, PA`,
  heroSubtitle: (t) =>
    `Premium roof replacement, repair, and free inspections for ${t.name} homeowners. ${t.stormExposure}`,
  introParagraphs: (t) => [
    `Graystone Roofing & Siding has been a trusted roofing contractor for ${t.name} homeowners and the surrounding ${t.county} area since 2007. Our team is personally involved in every project, with no high-pressure sales and honest recommendations, plus premium installation at a fair price.`,
    `${t.uniqueLocalContext} ${t.stormExposure}`,
    `${t.homeEra} ${t.treeCoverage} Whether your roof needs a full replacement, a targeted repair, or a pre-sale inspection, we tailor the system, architectural shingle, standing-seam metal, or flat-roof membrane, to your home, your roofline, and your budget.`,
  ],
  servicesHeading: (t) => `Roofing Services for ${t.name} Homeowners`,
  servicesOffered: (t) => [
    { label: "Full Roof Replacement", desc: `Complete tear-off and replacement with premium materials matched to ${t.name} homes and rooflines.` },
    { label: "Roof Repair", desc: "Fast, lasting repairs for leaks, storm damage, missing shingles, and flashing failures." },
    { label: "Architectural Shingles", desc: "Dimensional GAF shingles in dozens of colors, the best value for most homes." },
    { label: "Standing-Seam Metal Roofing", desc: `A durable, low-maintenance, long-term option well suited to ${t.county} homes.` },
    { label: "Storm Damage Repair", desc: `Wind and hail damage assessment with insurance documentation throughout ${t.county}.` },
    { label: "Free Roof Inspections", desc: `Thorough written inspection with photo documentation, free for ${t.name} homeowners.` },
    { label: "Flashing & Leak Repair", desc: "Chimneys, valleys, skylights, and penetrations sealed and re-flashed correctly." },
    { label: "Attic Ventilation", desc: "Ridge vents and balanced intake to reduce heat, moisture, and premature shingle wear." },
  ],
  problemsHeading: (t) => `Common Roofing Problems in ${t.name}`,
  problemsIntro: (t) =>
    `${t.stormExposure} ${t.treeCoverage} Those local conditions drive the roofing issues we see most often on ${t.name} homes, and here's how we address each one.`,
  commonProblems: [
    { problem: "Missing or lifted shingles after wind events", solution: "We re-secure or replace damaged shingles and check the surrounding field and edges for hidden uplift before it spreads." },
    { problem: "Leaks around chimneys, valleys, and flashing", solution: "We re-flash penetrations and valleys with proper materials and integrate ice-and-water shield where leaks start." },
    { problem: "Aging asphalt roofs losing granules", solution: "We assess remaining roof life honestly and recommend repair when sensible or full replacement when it's the smarter investment." },
    { problem: "Poor attic ventilation trapping heat and moisture", solution: "We balance intake and exhaust ventilation to extend shingle life and reduce attic moisture and ice-dam risk." },
    { problem: "Ice dams forming at the eaves in winter", solution: "We add ice-and-water shield at the eaves and improve ventilation and insulation coordination to keep the roof deck cold." },
  ],
  materialsHeading: (t) => `Roofing Materials for ${t.name} Homes`,
  materialOptions: [
    { name: "Architectural (Dimensional) Shingles", bestFor: "Most homes seeking the best balance of value and looks", benefits: "Wide color range, strong wind ratings, proven performance, manufacturer warranties", maintenance: "Low, occasional inspection and gutter clearing" },
    { name: "Standing-Seam Metal Roofing", bestFor: "Homeowners wanting maximum longevity and a clean modern or farmhouse look", benefits: "50+ year lifespan, sheds snow and debris, energy efficient, fire resistant", maintenance: "Very low, minimal upkeep over decades" },
    { name: "EPDM Rubber Membrane", bestFor: "Flat and low-slope roof sections, porches, and additions", benefits: "Seamless waterproofing on low slopes where shingles can't perform", maintenance: "Low, periodic seam and flashing checks" },
    { name: "Synthetic Underlayment & Ice-and-Water Shield", bestFor: "Every roof, as the critical layer beneath the surface", benefits: "Secondary moisture barrier at eaves, valleys, and penetrations", maintenance: "None, sealed beneath the roofing" },
    { name: "Ridge Vents & Ventilation", bestFor: "Homes with hot attics or shortened shingle life", benefits: "Lowers attic temperature and moisture, extends roof lifespan", maintenance: "None, built into the roof system" },
  ],
  pricingHeading: (t) => `What Affects Roofing Cost in ${t.name}?`,
  pricingIntro: (t) =>
    `Every ${t.name} roof is different, so we never quote sight-unseen. These are the factors that most influence the cost of a roof replacement or repair, all spelled out clearly in your written estimate.`,
  pricingFactors: [
    { factor: "Roof size (squares)", detail: "Total roof area is the biggest single driver of material and labor cost." },
    { factor: "Pitch & complexity", detail: "Steeper pitches and cut-up rooflines with many valleys, dormers, and hips take more time and care." },
    { factor: "Number of existing layers", detail: "Tearing off multiple old layers adds labor and disposal compared with a single layer." },
    { factor: "Material choice", detail: "Architectural shingle, metal, and membrane systems sit at different price points." },
    { factor: "Flashing & penetrations", detail: "Chimneys, skylights, and complex flashing details add scope." },
    { factor: "Ventilation upgrades", detail: "Adding ridge vents or balanced intake improves performance for a modest cost." },
    { factor: "Decking repairs", detail: "Rotted or soft decking discovered at tear-off is replaced for a sound foundation." },
    { factor: "Accessibility", detail: "Tight lots, tall rooflines, and landscaping can affect setup and labor." },
  ],
  processHeading: (t) => `Our Roofing Process in ${t.name}`,
  processSteps: [
    { title: "Request your free estimate", detail: `Call ${PHONE} or book online, we respond quickly to ${"every"} inquiry.` },
    { title: "On-site roof inspection", detail: "We inspect the roof, attic, flashing, and ventilation and document everything with photos." },
    { title: "Clear written proposal", detail: "You receive a detailed, itemized quote with materials, scope, and pricing spelled out." },
    { title: "Choose materials & colors", detail: "We help you select shingle, metal, or membrane systems and colors that fit your home." },
    { title: "Schedule installation", detail: "We set a date that works for you and prep to keep your home weathertight throughout." },
    { title: "Professional installation", detail: "Our crew completes most homes in 1-2 days with care taken to protect your property." },
    { title: "Cleanup & final walkthrough", detail: "We magnet-sweep for nails, haul all debris, and review the finished roof with you." },
  ],
  projectsHeading: (t) => `Example Roofing Projects Near ${t.name}`,
  // NOTE: Representative project TYPES we commonly handle, not specific completed jobs.
  // Replace with real ${t.name} case studies and project photos when available.
  projectExamples: (t) => [
    {
      title: `Storm Damage Roof Replacement, ${t.county}`,
      problem: "An aging architectural-shingle roof with granule loss and wind-lifted shingles began leaking near a chimney.",
      solution: "Full tear-off, new ice-and-water shield at eaves and valleys, re-flashed chimney, and balanced ridge ventilation.",
      materials: "GAF architectural shingles, synthetic underlayment, aluminum step flashing, ridge vent",
      result: "A weathertight, warrantied roof with improved attic ventilation and refreshed curb appeal.",
    },
    {
      title: `Metal Roof Upgrade on an Older ${t.name}-Area Home`,
      problem: "A homeowner with a steep, debris-prone roof wanted a durable, low-maintenance long-term solution.",
      solution: "Standing-seam metal installation with high-temperature underlayment and concealed fasteners.",
      materials: "Standing-seam metal panels, high-temp underlayment, color-matched trim",
      result: "A 50-year roof that sheds snow and debris and complements the home's character.",
    },
  ],
  planningHeading: (t) => `Roofing Planning Notes for ${t.name} Homeowners`,
  planningIntro: (t) =>
    `${t.planningNote} Requirements can vary by municipality and neighborhood, but we'll help you understand the right questions to ask before any work begins.`,
  planningPoints: [
    { title: "HOA & color guidelines", detail: "Some neighborhoods restrict shingle color or style, we help you choose approved, great-looking options." },
    { title: "Insurance documentation", detail: "After storms, we document damage thoroughly with photos to support your insurance claim." },
    { title: "Ventilation & code", detail: "We design ventilation to meet code and protect your warranty and roof lifespan." },
  ],
  faqs: (t) => {
    const faqs: FAQ[] = [
      {
        question: `How much does a new roof cost in ${t.name}, PA?`,
        answer: `Roof replacement in ${t.name} typically ranges from $8,000-$18,000 for a standard home with architectural shingles, depending on roof size, pitch, layers to remove, and material choice. Contact Graystone for a free, detailed written estimate specific to your home.`,
      },
      {
        question: `How do I know if my ${t.name} roof needs repair or replacement?`,
        answer: `Telltale signs include shingles that are curling, cracking, or shedding granules; an age over 20-25 years; recurring leaks or ceiling stains; visible sagging; and storm damage. We offer free inspections in ${t.name} and give you an honest assessment of repair versus replacement.`,
      },
      {
        question: `What roofing materials work best for ${t.name} homes?`,
        answer: isHistoric(t)
          ? `Architectural shingles suit most ${t.name} homes for value and performance. Given the area's historic and traditional character, we also discuss standing-seam metal, which complements older architecture while delivering decades of low-maintenance protection.`
          : `For most ${t.name} homes, GAF architectural shingles offer the best mix of durability and value. Standing-seam metal roofing is an excellent long-term upgrade we install throughout ${t.county}.`,
      },
      {
        question: `Can you help with storm-related roof damage and insurance claims?`,
        answer: `Yes. ${t.stormExposure} When storms hit, we inspect the roof, document wind or hail damage with photos, and provide the detailed records you need to support an insurance claim.`,
      },
      {
        question: `How long does a roof replacement take in ${t.name}?`,
        answer: `Most standard residential roof replacements are completed in 1-2 days. We schedule ${t.name} projects to minimize disruption and keep your home weathertight throughout the process.`,
      },
      {
        question: `Are you licensed and insured in ${t.county}?`,
        answer: `Yes. Graystone holds PA Home Improvement Contractor license #PA034872 and carries full liability and workers' compensation insurance. We serve all of ${t.county} from our East Petersburg base.`,
      },
      {
        question: `Do you offer free roof inspections in ${t.name}?`,
        answer: `Absolutely. Our ${t.name} roof inspections are free, with no obligation. You'll receive a written assessment and photo documentation so you can make an informed decision.`,
      },
    ];
    if (isTreeHeavy(t)) {
      faqs.push({
        question: `Will algae-resistant shingles help on shaded ${t.name} roofs?`,
        answer: `Yes. ${t.treeCoverage} Algae-resistant architectural shingles resist the dark streaking that develops on damp, shaded north-facing slopes, a worthwhile upgrade for tree-heavy lots in ${t.name}.`,
      });
    }
    return faqs;
  },
  midCTA: (t) => ({
    title: `Free Roof Inspection in ${t.name}`,
    subtitle: `Call Graystone Roofing directly at ${PHONE} or schedule your free inspection online. Serving all of ${t.county}.`,
  }),
  finalCTA: (t) => ({
    title: `Ready for a New Roof in ${t.name}?`,
    subtitle: `Get a free, no-pressure inspection and a detailed written estimate. Call ${PHONE} or schedule online, proudly serving ${t.name} and all of ${t.county}.`,
  }),
  relatedLinks: (t) => [
    { label: "Roofing Services Overview", href: "/services/roofing-company-lancaster-pa/" },
    { label: "Roof Repair", href: "/services/roof-repair-lancaster-pa/" },
    { label: "Metal Roofing", href: "/services/metal-roofing-lancaster-pa/" },
    { label: "Storm Damage Repair", href: "/services/roofing-storm-damage-lancaster-pa/" },
    { label: `Siding Contractor ${t.name}`, href: sidingRoute(t) },
    { label: `Gutter Installation ${t.name}`, href: guttersRoute(t) },
    { label: "Schedule a Free Inspection", href: "/contact-graystone-roofing-siding/" },
  ],
};

const siding: GeoServiceConfig = {
  key: "siding",
  label: "Siding",
  iconKey: "home",
  schemaType: ["LocalBusiness", "HomeAndConstructionBusiness"],
  seoTitle: (t) => geoTitle(`Siding Contractor in ${t.name}, PA`, "Installation & Repair"),
  seoDescription: (t) =>
    `Vinyl, insulated, and cedar-shake siding installation in ${t.name}, PA with expert trim, soffit, and fascia. Free Graystone estimates: ${PHONE}.`,
  canonical: (t) =>
    `https://lancasterparoofing.com/service-areas/siding-contractor-${t.slug}-pa/`,
  breadcrumbs: (t) => [
    { label: "Services", href: "/services/" },
    { label: "Siding", href: "/services/siding-contractor-lancaster-pa/" },
    { label: `${t.name}, PA` },
  ],
  h1: (t) => `Siding Contractor in ${t.name}, PA`,
  heroSubtitle: (t) =>
    `Premium vinyl, insulated, and cedar-shake siding for ${t.name} homeowners. ${t.housingCharacter} We help you choose a look that fits right in.`,
  introParagraphs: (t) => [
    `Graystone Roofing & Siding brings premium siding installation to ${t.name} homeowners throughout ${t.county}. When you work with Graystone, you're working with a locally owned, owner-operated company, not a franchise and not a national chain.`,
    `${t.housingCharacter} ${t.planningNote}`,
    `${t.homeEra} ${t.treeCoverage} Every installation includes proper house wrap, correct technique that allows for thermal expansion, and meticulous trim work at windows, doors, and corners, the details that separate a siding job that lasts from one that fails early.`,
  ],
  servicesHeading: (t) => `Siding Services for ${t.name} Homes`,
  servicesOffered: (t) => [
    { label: "Siding Replacement", desc: `Full removal and replacement with premium materials matched to ${t.name} homes.` },
    { label: "Premium Vinyl Siding", desc: `Quality .044\"-.046\" vinyl in dozens of colors and profiles.` },
    { label: "Insulated Vinyl Siding", desc: "Foam-backed panels for better energy efficiency and reduced sound transmission." },
    { label: "Cedar-Shake & Board-and-Batten", desc: `Shake and vertical profiles popular in ${t.county}'s architectural vernacular.` },
    { label: "Siding Repair", desc: "Targeted repairs for cracked, warped, or storm-damaged panels." },
    { label: "Soffit & Fascia", desc: "Low-maintenance aluminum and vinyl soffit and fascia that protect the roofline." },
    { label: "Custom Trim Work", desc: "J-channel, corner posts, window casing, and door trim, done right." },
    { label: "House Wrap & Moisture Barrier", desc: "Proper weather-resistant barrier installed with every full siding project." },
  ],
  problemsHeading: (t) => `Common Siding Problems in ${t.name}`,
  problemsIntro: (t) =>
    `${t.treeCoverage} Combined with the age of many local homes, that's what drives the siding issues we see most often in ${t.name}, and how we fix them.`,
  commonProblems: [
    { problem: "Warped, cracked, or brittle panels", solution: "We replace failing panels and, where it's the better value, re-side the home with durable, modern vinyl." },
    { problem: "Faded color or chalky surface", solution: "We install fade-resistant siding in a color you'll love, restoring curb appeal in a couple of days." },
    { problem: "Moisture or rot behind the siding", solution: "We address the source, install proper house wrap and flashing, and re-side to keep walls dry." },
    { problem: "Storm-damaged or loose panels", solution: "We repair or replace damaged sections and verify fastening so panels hold in high wind." },
    { problem: "Rotted trim, soffit, or fascia", solution: "We replace wood trim with low-maintenance materials that resist rot and protect the roofline." },
    { problem: "Outdated, tired curb appeal", solution: "We modernize the exterior with fresh profiles, colors, and accents that fit the neighborhood." },
  ],
  materialsHeading: (t) => `Siding Materials for ${t.name} Homes`,
  materialOptions: [
    { name: "Premium Vinyl Siding", bestFor: "Most homes seeking durability, color choice, and great value", benefits: "Dozens of colors and profiles, fade resistant, never needs painting", maintenance: "Very low, an occasional rinse" },
    { name: "Insulated Vinyl Siding", bestFor: "Homeowners focused on energy efficiency and comfort", benefits: "Added R-value, improved impact resistance, quieter walls", maintenance: "Very low, an occasional rinse" },
    { name: "Cedar-Shake Vinyl", bestFor: "Traditional, historic, and farmhouse-style homes", benefits: "Classic shake look without the upkeep of real cedar", maintenance: "Low, far less than natural wood" },
    { name: "Board & Batten", bestFor: "Gable ends, dormers, and modern-farmhouse accents", benefits: "Vertical profile adds architectural interest and curb appeal", maintenance: "Very low" },
    { name: "Soffit, Fascia & Trim", bestFor: "Completing and protecting the full exterior system", benefits: "Aluminum and vinyl resist rot, finish the look, and protect the roofline", maintenance: "Very low" },
  ],
  pricingHeading: (t) => `What Affects Siding Cost in ${t.name}?`,
  pricingIntro: (t) =>
    `Siding cost depends on your home and the look you want. These are the factors we account for in every ${t.name} estimate, itemized clearly so you know exactly what you're paying for.`,
  pricingFactors: [
    { factor: "Home size & wall area", detail: "Total square footage of wall to cover is the primary cost driver." },
    { factor: "Material & profile", detail: "Standard vinyl, insulated vinyl, cedar-shake, and board-and-batten sit at different price points." },
    { factor: "Removal of old siding", detail: "Tearing off and disposing of existing siding adds labor versus a clean substrate." },
    { factor: "Trim & detail work", detail: "Window casing, corners, and accent areas add craftsmanship and time." },
    { factor: "Insulation upgrades", detail: "Adding insulated siding or rigid foam improves efficiency for a modest added cost." },
    { factor: "Windows & doors", detail: "More openings mean more flashing, J-channel, and detail work." },
    { factor: "Soffit & fascia", detail: "Including soffit and fascia completes and protects the exterior system." },
    { factor: "Number of stories & access", detail: "Taller homes and tight lots affect staging and labor." },
  ],
  processHeading: (t) => `Our Siding Process in ${t.name}`,
  processSteps: [
    { title: "Request your free estimate", detail: `Call ${PHONE} or book online, we respond quickly.` },
    { title: "On-site exterior assessment", detail: "We measure, inspect the wall sheathing, trim, soffit, and fascia, and review your goals." },
    { title: "Clear written proposal", detail: "You receive a detailed, itemized quote with materials, profiles, and scope spelled out." },
    { title: "Choose siding, profiles & colors", detail: "We help you pick the material, profile, and color that fit your home and neighborhood." },
    { title: "Schedule installation", detail: "We set a date that works for you and protect landscaping and the work area." },
    { title: "Professional installation", detail: "House wrap, correct expansion technique, and meticulous trim, most homes in 2-4 days." },
    { title: "Cleanup & final walkthrough", detail: "We clean the site thoroughly and review the finished exterior with you." },
  ],
  projectsHeading: (t) => `Example Siding Projects Near ${t.name}`,
  // NOTE: Representative project TYPES we commonly handle, not specific completed jobs.
  // Replace with real ${t.name} case studies and project photos when available.
  projectExamples: (t) => [
    {
      title: `Full Re-Side & Trim Refresh, ${t.county}`,
      problem: "Faded, brittle siding and rotted wood trim left a home looking dated and let moisture reach the walls.",
      solution: "Complete tear-off, new house wrap, premium vinyl siding, and low-maintenance aluminum trim, soffit, and fascia.",
      materials: "Premium .046\" vinyl siding, house wrap, aluminum soffit and fascia, custom trim",
      result: "A weather-tight, modern exterior with a dramatic boost in curb appeal and no more painting.",
    },
    {
      title: `Cedar-Shake Accent Upgrade on an Older ${t.name}-Area Home`,
      problem: "A traditional home needed an exterior refresh that honored its character without the upkeep of real wood.",
      solution: "Cedar-shake vinyl on gables and accents paired with clean lap siding on the main walls.",
      materials: "Cedar-shake vinyl, lap siding, board-and-batten accents, color-matched trim",
      result: "A timeless, low-maintenance look that fits right into the neighborhood.",
    },
  ],
  planningHeading: (t) => `Siding Planning Notes for ${t.name} Homeowners`,
  planningIntro: (t) =>
    `${t.planningNote} Requirements can vary by municipality and neighborhood, but we'll help you understand what to confirm before work begins.`,
  planningPoints: [
    { title: "HOA exterior guidelines", detail: "Many communities approve specific colors and profiles, we help you stay compliant while loving the result." },
    { title: "Historic & older-home aesthetics", detail: "We match profile proportions and colors that respect the character of older homes." },
    { title: "Weather-resistant barriers", detail: "Proper house wrap and flashing keep walls dry and protect your investment for the long term." },
  ],
  faqs: (t) => {
    const faqs: FAQ[] = [
      {
        question: `How much does vinyl siding cost in ${t.name}, PA?`,
        answer: `Vinyl siding installation in ${t.name} typically ranges from $7,000-$15,000 for a standard home, depending on home size, the profile chosen, and whether insulated siding is selected. Contact Graystone for a free, detailed written estimate.`,
      },
      {
        question: `What is the best siding for ${t.name} homes?`,
        answer: isHistoric(t)
          ? `For ${t.name}'s many historic and traditional homes, we often recommend cedar-shake vinyl or a classic lap profile that matches original proportions. Premium .044\"-.046\" vinyl delivers durability and low maintenance while keeping the right look.`
          : `For most ${t.name} homes, premium vinyl at .044\"-.046\" thickness offers the best mix of durability, low maintenance, and value. Insulated vinyl is popular here for its added energy efficiency.`,
      },
      {
        question: `Can new siding improve curb appeal and energy efficiency?`,
        answer: `Yes, significantly. New siding transforms a home's appearance, and insulated vinyl or added rigid-foam board improves wall comfort and energy efficiency. ${t.housingCharacter}`,
      },
      {
        question: `Do you replace trim, soffit, and fascia too?`,
        answer: `Absolutely. We handle the complete exterior, siding, custom trim, and low-maintenance soffit and fascia, so the whole system looks right and protects your roofline.`,
      },
      {
        question: `How do I know if moisture is getting behind my ${t.name} siding?`,
        answer: `Warning signs include bubbling or blistering panels, soft or rotted trim, interior wall staining, and a musty smell. ${t.treeCoverage} We inspect for moisture intrusion and correct the source before re-siding.`,
      },
      {
        question: `How long does siding installation take in ${t.name}?`,
        answer: `Most standard ${t.name} siding projects are completed in 2-4 days depending on home size and detail. We minimize disruption and leave your property clean every day.`,
      },
      {
        question: `Are you licensed and insured in ${t.county}?`,
        answer: `Yes. Graystone holds PA Home Improvement Contractor license #PA034872 with full liability and workers' compensation insurance, serving all of ${t.county} from East Petersburg.`,
      },
    ];
    return faqs;
  },
  midCTA: (t) => ({
    title: `Free Siding Estimate in ${t.name}`,
    subtitle: `Call ${PHONE} or schedule your free estimate. We serve all of ${t.county} and surrounding areas.`,
  }),
  finalCTA: (t) => ({
    title: `Ready for New Siding in ${t.name}?`,
    subtitle: `Get a free, no-pressure estimate and a detailed written quote. Call ${PHONE} or schedule online, proudly serving ${t.name} and all of ${t.county}.`,
  }),
  relatedLinks: (t) => [
    { label: "Siding Services Overview", href: "/services/siding-contractor-lancaster-pa/" },
    { label: `Gutter Installation ${t.name}`, href: guttersRoute(t) },
    { label: `Roofing Contractor ${t.name}`, href: roofingRoute(t) },
    { label: "All Exterior Services", href: "/services/" },
    { label: "Get a Free Estimate", href: "/contact-graystone-roofing-siding/" },
  ],
};

const gutters: GeoServiceConfig = {
  key: "gutters",
  label: "Gutters",
  iconKey: "droplets",
  schemaType: ["LocalBusiness", "HomeAndConstructionBusiness"],
  seoTitle: (t) => geoTitle(`Gutter Installation in ${t.name}, PA`, "Seamless Gutters"),
  seoDescription: (t) =>
    `Seamless aluminum gutter installation, repair, and gutter guards in ${t.name}, PA. Color-matched systems that protect your foundation. Call ${PHONE}.`,
  canonical: (t) =>
    `https://lancasterparoofing.com/service-areas/gutter-installation-${t.slug}-pa/`,
  breadcrumbs: (t) => [
    { label: "Services", href: "/services/" },
    { label: "Gutters", href: "/services/gutter-installation-lancaster-pa-york-pa/" },
    { label: `${t.name}, PA` },
  ],
  h1: (t) => `Gutter Installation in ${t.name}, PA`,
  heroSubtitle: (t) =>
    `Seamless aluminum gutters and gutter guards custom-fit to ${t.name} homes. ${t.treeCoverage} Keep water, and debris, under control.`,
  introParagraphs: (t) => [
    `Graystone Roofing & Siding installs seamless aluminum gutters throughout ${t.county}, including ${t.name}. Our gutters are fabricated on-site to fit your home exactly, no seams in the run, no joint failures, and no overflow.`,
    `${t.treeCoverage} ${t.terrainDrainage}`,
    `${t.stormExposure} Every installation includes correct slope for drainage, properly sized downspouts, color-matched options, and quality gutter guard systems to minimize ongoing maintenance and protect your foundation.`,
  ],
  servicesHeading: (t) => `Gutter Services for ${t.name} Homes`,
  servicesOffered: (t) => [
    { label: "Seamless Aluminum Gutters", desc: "On-site fabricated seamless runs, no seams, no joint failures, clean appearance." },
    { label: "5\" & 6\" Gutter Sizing", desc: `Properly sized for your ${t.name} home's roof area, pitch, and rainfall volume.` },
    { label: "Gutter Guards", desc: "Quality leaf-protection systems that cut cleaning and prevent debris blockage." },
    { label: "Downspouts & Extensions", desc: "Correctly sized downspouts and extensions that discharge water away from the foundation." },
    { label: "Drainage Correction", desc: "We fix overflow, pooling, and grading issues that send water toward the house." },
    { label: "Color-Matched Systems", desc: "Dozens of colors to complement your home's fascia and siding." },
    { label: "Fascia Repair", desc: "We replace rotted fascia so gutters hang straight and stay secure." },
    { label: "Full System Replacement", desc: "Complete gutters, downspouts, and splash blocks from one contractor." },
  ],
  problemsHeading: (t) => `Common Gutter Problems in ${t.name}`,
  problemsIntro: (t) =>
    `${t.treeCoverage} ${t.terrainDrainage} Those conditions are exactly why gutters fail in ${t.name}, and here's how we put it right.`,
  commonProblems: [
    { problem: "Gutters overflowing during moderate rain", solution: "We right-size gutters (5\" or 6\") and downspouts to the roof area so they handle real rainfall volume." },
    { problem: "Poor downspout drainage near the foundation", solution: "We add properly sized downspouts and extensions that carry water well away from the home." },
    { problem: "Sagging gutters pulling from the fascia", solution: "We re-hang with hidden heavy-gauge hangers and replace any rotted fascia for a solid mount." },
    { problem: "Constant clogging from tree debris", solution: "We install quality gutter guards so leaves and needles stay out and water keeps flowing." },
    { problem: "Water pooling near the foundation", solution: "We correct slope and discharge so runoff drains away instead of seeping toward the basement." },
    { problem: "Rotted fascia behind the gutters", solution: "We replace damaged fascia and flash the roof edge correctly before installing new gutters." },
  ],
  materialsHeading: (t) => `Gutter Options for ${t.name} Homes`,
  materialOptions: [
    { name: "5\" Seamless Aluminum", bestFor: "Most homes with standard roof area and pitch", benefits: "Seamless run, color-matched, durable, no joint leaks", maintenance: "Low, periodic clearing without guards" },
    { name: "6\" Oversized Seamless", bestFor: "Larger roofs, steep pitches, and heavy-rain exposure", benefits: "Handles far more water volume; pairs with larger downspouts", maintenance: "Low" },
    { name: "Gutter Guards", bestFor: "Tree-heavy lots and hard-to-reach rooflines", benefits: "Keeps debris out, reduces cleaning, prevents clogs and overflow", maintenance: "Minimal, occasional surface rinse" },
    { name: "Downspouts & Extensions", bestFor: "Protecting the foundation and landscaping", benefits: "Carries water away from the home to prevent seepage and erosion", maintenance: "Low" },
    { name: "Color-Matched Fascia & Hangers", bestFor: "A clean, integrated, long-lasting installation", benefits: "Heavy-gauge hidden hangers and matching fascia for a finished look", maintenance: "Very low" },
  ],
  pricingHeading: (t) => `What Affects Gutter Cost in ${t.name}?`,
  pricingIntro: (t) =>
    `Gutter cost comes down to the size of the system and a few key details. Here's what we factor into every ${t.name} estimate, spelled out clearly.`,
  pricingFactors: [
    { factor: "Linear footage", detail: "The total length of gutter run is the main cost driver." },
    { factor: "Number of stories", detail: "Taller homes require more staging and labor to install safely." },
    { factor: "Gutter size & type", detail: "5\" versus 6\" seamless aluminum affects both material and capacity." },
    { factor: "Downspouts", detail: "The number and size of downspouts needed to handle the volume." },
    { factor: "Gutter guards", detail: "Adding leaf protection increases upfront cost but reduces long-term maintenance." },
    { factor: "Fascia repairs", detail: "Replacing rotted fascia ensures a solid, secure mounting surface." },
    { factor: "Drainage needs", detail: "Extensions, splash blocks, or grading work to move water away from the home." },
  ],
  processHeading: (t) => `Our Gutter Process in ${t.name}`,
  processSteps: [
    { title: "Request your free estimate", detail: `Call ${PHONE} or book online, we respond quickly.` },
    { title: "On-site gutter & drainage assessment", detail: "We measure runs, check pitch, downspouts, and fascia, and identify any drainage issues." },
    { title: "Clear written proposal", detail: "You receive a detailed, itemized quote with sizing, guards, color, and scope spelled out." },
    { title: "Choose size, color & guards", detail: "We help you select 5\" or 6\" gutters, a matching color, and whether to add guards." },
    { title: "Schedule installation", detail: "We set a convenient date, most gutter installations are completed in a single day." },
    { title: "On-site seamless fabrication & install", detail: "We roll seamless runs on-site for an exact fit, then hang with heavy-gauge hangers." },
    { title: "Cleanup & final walkthrough", detail: "We clean up completely and confirm proper flow and discharge with you." },
  ],
  projectsHeading: (t) => `Example Gutter Projects Near ${t.name}`,
  // NOTE: Representative project TYPES we commonly handle, not specific completed jobs.
  // Replace with real ${t.name} case studies and project photos when available.
  projectExamples: (t) => [
    {
      title: `Seamless Gutter & Guard Install, ${t.county}`,
      problem: "Undersized, clog-prone gutters overflowed in storms and sent water toward the foundation.",
      solution: "New 6\" seamless aluminum gutters with oversized downspouts, gutter guards, and corrected discharge.",
      materials: "6\" seamless aluminum, oversized downspouts, gutter guards, hidden hangers",
      result: "No more overflow, far less cleaning, and water reliably directed away from the home.",
    },
    {
      title: `Fascia Repair & Re-Gutter on an Older ${t.name}-Area Home`,
      problem: "Sagging gutters had pulled away from rotted fascia, letting water run behind them.",
      solution: "Replaced damaged fascia, re-flashed the roof edge, and installed new seamless gutters.",
      materials: "Aluminum fascia, seamless gutters, heavy-gauge hangers, color-matched trim",
      result: "Straight, secure gutters on a solid roofline that protect the home for decades.",
    },
  ],
  planningHeading: (t) => `Gutter Planning Notes for ${t.name} Homeowners`,
  planningIntro: (t) =>
    `${t.terrainDrainage} ${t.planningNote} We'll help you understand the right questions to ask before any work begins.`,
  planningPoints: [
    { title: "Downspout placement", detail: "Where downspouts discharge matters, we route water away from the foundation and walkways." },
    { title: "Foundation drainage", detail: "Proper extensions and grading prevent the basement seepage and erosion that bad gutters cause." },
    { title: "Fascia condition", detail: "We check and repair fascia so new gutters mount securely and stay level." },
    { title: "Gutter guards for tree-heavy lots", detail: "On wooded properties, guards are often the smartest long-term investment." },
  ],
  faqs: (t) => {
    const faqs: FAQ[] = [
      {
        question: `How much does gutter installation cost in ${t.name}, PA?`,
        answer: `Seamless aluminum gutter installation in ${t.name} typically costs $800-$2,500, depending on linear footage, gutter size (5\" vs. 6\"), and downspout configuration. Adding gutter guards increases the cost but reduces long-term maintenance. Contact Graystone for a free, specific estimate.`,
      },
      {
        question: `What size gutters does my ${t.name} home need?`,
        answer: `5\" gutters are standard for most homes. For ${t.name} homes with a larger roof area or steeper pitch, 6\" gutters handle volume more effectively and are well worth the modest additional cost.`,
      },
      {
        question: `Do I need gutter guards if I have trees near my house?`,
        answer: isTreeHeavy(t)
          ? `Most likely, yes. ${t.treeCoverage} On tree-heavy ${t.name} lots, gutter guards dramatically reduce clogging and overflow and are one of the best long-term investments you can make.`
          : `It depends on your tree cover. For ${t.name} homes near mature trees, gutter guards reduce cleaning and prevent the clogs that cause overflow, a smart, optional upgrade.`,
      },
      {
        question: `Can poor gutters cause foundation problems in ${t.name}?`,
        answer: `Yes. ${t.terrainDrainage} When gutters overflow or discharge too close to the house, water pools against the foundation and can lead to seepage and erosion. Proper sizing, slope, and downspout extensions prevent it.`,
      },
      {
        question: `How long do gutters last in ${t.county}?`,
        answer: `Properly installed seamless aluminum gutters last 20-30 years. The most common failure modes are improper slope, undersized downspouts, and poor corner sealing, all of which we address carefully on every installation.`,
      },
      {
        question: `Do you replace fascia if it's damaged?`,
        answer: `Yes. If we find rotted or damaged fascia behind your gutters, we replace it so the new system mounts to a solid, level surface and stays secure for the long haul.`,
      },
      {
        question: `How do I know when gutters need replacement in ${t.name}?`,
        answer: `Watch for gutters sagging or pulling away from the fascia, visible rust or holes, consistent overflow in moderate rain, foundation staining, and an age over 20 years. We'll assess your current system during a free inspection.`,
      },
    ];
    return faqs;
  },
  midCTA: (t) => ({
    title: `Free Gutter Estimate in ${t.name}`,
    subtitle: `Call ${PHONE} or schedule your free assessment. We serve all of ${t.county}.`,
  }),
  finalCTA: (t) => ({
    title: `Ready for New Gutters in ${t.name}?`,
    subtitle: `Protect your foundation with a properly sized, seamless system. Call ${PHONE} or schedule online, proudly serving ${t.name} and all of ${t.county}.`,
  }),
  relatedLinks: (t) => [
    { label: "Gutter Services Overview", href: "/services/gutter-installation-lancaster-pa-york-pa/" },
    { label: `Siding Contractor ${t.name}`, href: sidingRoute(t) },
    { label: `Roofing Contractor ${t.name}`, href: roofingRoute(t) },
    { label: "All Exterior Services", href: "/services/" },
    { label: "Get a Free Estimate", href: "/contact-graystone-roofing-siding/" },
  ],
};

export const geoServices: Record<ServiceKey, GeoServiceConfig> = {
  roofing,
  siding,
  gutters,
};
